import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { useCreateUserReport } from "@/lib/api/user-reports";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";

const MENTOR_REPORT_CATEGORIES = [
  {
    value: "HARASSMENT_ABUSIVE_LANGUAGE",
    label: "Harassment or abusive language toward me",
  },
  { value: "THREATS_INTIMIDATION", label: "Threats or intimidation" },
  {
    value: "SEXUAL_INAPPROPRIATE_MESSAGES",
    label: "Sexual or inappropriate messages",
  },
  {
    value: "EXPLICIT_SEXUAL_IMAGES",
    label: "Shared explicit or sexual images",
  },
  { value: "HATE_SPEECH_SLURS", label: "Hate speech or slurs" },
  {
    value: "BOUNDARY_CROSSING",
    label: "Kept crossing boundaries after I asked them to stop",
  },
  {
    value: "CONTACT_OUTSIDE_APP",
    label: "Tried to contact me outside the app",
  },
  { value: "OTHER_POLICY_VIOLATION", label: "Other policy violation" },
] as const;

interface ReportTeenModalProps {
  teenName: string;
  mentorshipId?: string;
  open: boolean;
  onClose: () => void;
}

export function ReportTeenModal({
  teenName,
  mentorshipId,
  open,
  onClose,
}: ReportTeenModalProps) {
  const [category, setCategory] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const { mutate, isPending } = useCreateUserReport();

  const resetState = () => {
    setCategory("");
    setNote("");
    setSubmitted(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetState, 200);
  };

  const handleSubmit = () => {
    if (!mentorshipId) return;
    mutate(
      {
        mentorshipId,
        category: category || undefined,
        freeText: note || undefined,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) handleClose();
      }}
    >
      <DialogContent className="z-[50] w-full max-w-lg">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold">
                Report
              </DialogTitle>
            </DialogHeader>
            <Separator className="-mx-6 w-auto" />
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <span className="text-lg font-semibold text-slate-900">
                Report has been sent
              </span>
              <p className="text-base text-black">
                Your supervisor will review this with our internal team.
                We&apos;ll reach out if we have any questions.
              </p>
            </div>
            <Button
              className="w-full bg-black text-base font-bold text-white hover:bg-gray-800"
              onClick={handleClose}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <DialogHeader className="space-y-0">
              <DialogTitle className="text-2xl font-semibold">
                Report {teenName}
              </DialogTitle>
            </DialogHeader>
            <Separator className="-mx-6 w-auto" />
            <DialogDescription className="text-base text-black">
              Use this to report your mentee for conduct that violates platform
              rules (e.g. harassment, threats, or inappropriate behavior towards
              you).
              <span className="mt-1 block text-sm italic text-zinc-600">
                For clinical concerns about their wellbeing, use escalation
                instead.
              </span>
            </DialogDescription>

            <div className="flex flex-col gap-2">
              <Label htmlFor="report-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="report-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {MENTOR_REPORT_CATEGORIES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="report-note">Note</Label>
              <Textarea
                id="report-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Tell us more (optional)"
                rows={4}
              />
            </div>

            <div className="mt-2 flex flex-row items-center gap-4">
              <Button
                className="w-full font-bold text-slate-900"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                className="w-full bg-black font-bold text-white hover:bg-gray-800"
                onClick={handleSubmit}
                disabled={isPending || !mentorshipId}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
